/**
 * EditorErrorBoundary — sl-e0il.1.3
 *
 * Catches render errors from the lazy-loaded editor components
 * (chunk-load failure, CodeMirror runtime crash). Renders a fixed
 * i18n fallback: "Editor konnte nicht geladen werden, bitte Seite
 * neu laden." There is intentionally NO textarea fallback — sl-e0il
 * is a Zero-Fallbacks epic, and a silent degradation path would hide
 * regressions where CodeMirror silently refuses to load.
 *
 * Consumers wrap every `<Lazy*Editor>` in this boundary + a Suspense
 * fallback (see `lazy.ts`).
 */

import { useI18n } from '@affine/i18n';
import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';

import * as styles from './editor.css';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class EditorErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log to the browser console so it surfaces in dev + Sentry picks it
    // up in production. We deliberately do not re-throw or fall back.

    console.error('[sl-e0il] Editor failed to load/render:', error, info);
  }

  override render(): ReactNode {
    if (this.state.error) {
      return <EditorErrorFallback />;
    }
    return this.props.children;
  }
}

/** Separate functional component so we can call the i18n hook. */
function EditorErrorFallback() {
  const t = useI18n();
  return (
    <div role="alert" className={styles.editorErrorFallback}>
      {t['com.streamlain.streamlain.workflow.editor.load-error']()}
    </div>
  );
}
